type DividerSpacing = "none" | "sm" | "md" | "lg";

const spacingClasses: Record<DividerSpacing, string> = {
  none: "",
  sm: "my-1",
  md: "my-3",
  lg: "my-6",
};

// Vertical spacing is horizontal margin, and the height comes from the Row it
// sits in — `self-stretch` unless a fixed `height` is given.
const verticalSpacingClasses: Record<DividerSpacing, string> = {
  none: "",
  sm: "mx-1",
  md: "mx-3",
  lg: "mx-6",
};

const heightClasses = {
  full: "self-stretch",
  text: "h-4",
  control: "h-8",
} as const;

/**
 * A hairline separating content. Use it to show that stacked items are separate
 * rows (a todo list, a run of settings) when a full Panel or PanelSection would
 * be too heavy.
 *
 * `spacing` controls the margin on either side. Default `none` leaves the
 * spacing to the surrounding Stack or Row, which is usually what you want.
 *
 * `vertical` turns it into a divider *between* items in a Row — a 1px column
 * rather than an `<hr>`. `height` sizes it: `full` stretches to the Row (the
 * default), `text` matches a line of text, `control` matches a 32px control.
 */
export function Divider({
  spacing = "none",
  vertical,
  height = "full",
}: {
  spacing?: DividerSpacing;
  vertical?: boolean;
  height?: keyof typeof heightClasses;
}) {
  if (vertical) {
    // A div, not an <hr>: an <hr> is semantically a thematic break in flow
    // content, which a decorative gap between two controls is not.
    return (
      <div
        aria-hidden
        className={`w-px shrink-0 bg-border ${heightClasses[height]} ${verticalSpacingClasses[spacing]}`}
      />
    );
  }

  return (
    <hr
      className={`border-0 border-t border-border ${spacingClasses[spacing]}`}
    />
  );
}
